import { useState, useEffect, useCallback } from "react";
import api from "../services/api";

const SELECTED_KEY = "erpSelectedCliente";

/** Last farmacia the agent picked for a phone shared by several clientes. */
const readSelectedClienteId = (phone) => {
  try {
    const stored = JSON.parse(localStorage.getItem(SELECTED_KEY) || "{}");
    return stored[phone] || null;
  } catch {
    return null;
  }
};

const storeSelectedClienteId = (phone, clienteId) => {
  try {
    const stored = JSON.parse(localStorage.getItem(SELECTED_KEY) || "{}");
    stored[phone] = clienteId;
    localStorage.setItem(SELECTED_KEY, JSON.stringify(stored));
  } catch {
    // selection just won't be remembered
  }
};

/**
 * Resolves ERP client by contact phone when ERP integration is configured.
 */
const useErpClient = (phone) => {
  const [erpConfigured, setErpConfigured] = useState(false);
  const [erpClient, setErpClient] = useState(null);
  const [erpClients, setErpClients] = useState([]);
  const [erpFrontendUrl, setErpFrontendUrl] = useState(null);
  const [erpPendingCounts, setErpPendingCounts] = useState({
    facturasPendientes: 0,
    ordenesPendientes: 0
  });
  const [loading, setLoading] = useState(true);
  const phoneKey = phone || "";
  const [seenPhone, setSeenPhone] = useState(phoneKey);
  if (phoneKey !== seenPhone) {
    setSeenPhone(phoneKey);
    setErpClient(null);
    setErpClients([]);
    setErpPendingCounts({ facturasPendientes: 0, ordenesPendientes: 0 });
    setLoading(!!phoneKey);
  }

  useEffect(() => {
    if (!phone) {
      setErpConfigured(false);
      setErpClient(null);
      setErpFrontendUrl(null);
      setErpPendingCounts({ facturasPendientes: 0, ordenesPendientes: 0 });
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const statusRes = await api.get("/erp/status");
        const configured = !!statusRes.data?.configured;
        if (cancelled) return;
        setErpConfigured(configured);
        setErpFrontendUrl(statusRes.data?.frontendUrl || null);

        if (!configured) {
          setErpClient(null);
          setErpPendingCounts({ facturasPendientes: 0, ordenesPendientes: 0 });
          return;
        }

        try {
          const clientRes = await api.get("/erp/client", {
            params: { phone }
          });
          if (!cancelled) {
            const primary = clientRes.data?.cliente || null;
            const clientes =
              Array.isArray(clientRes.data?.clientes) &&
              clientRes.data.clientes.length
                ? clientRes.data.clientes
                : primary
                ? [primary]
                : [];
            const rememberedId = readSelectedClienteId(phone);
            const cliente =
              clientes.find((c) => c.clienteId === rememberedId) || primary;
            setErpClients(clientes);
            setErpClient(cliente);
          }
        } catch (err) {
          if (!cancelled) {
            setErpClient(null);
            setErpClients([]);
          }
        }
      } catch (_err) {
        if (!cancelled) {
          setErpConfigured(false);
          setErpClient(null);
          setErpFrontendUrl(null);
          setErpPendingCounts({ facturasPendientes: 0, ordenesPendientes: 0 });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [phone]);

  // Pending counts follow the selected cliente
  const selectedClienteId = erpClient?.clienteId || null;
  useEffect(() => {
    setErpPendingCounts({ facturasPendientes: 0, ordenesPendientes: 0 });
    if (!selectedClienteId) return undefined;
    let cancelled = false;
    api
      .get(`/erp/clientes/${selectedClienteId}/pending-counts`)
      .then((countsRes) => {
        if (!cancelled && countsRes.data?.configured) {
          setErpPendingCounts({
            facturasPendientes: countsRes.data.facturasPendientes ?? 0,
            ordenesPendientes: countsRes.data.ordenesPendientes ?? 0
          });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedClienteId]);

  const selectErpClient = useCallback(
    (clienteId) => {
      const next = erpClients.find((c) => c.clienteId === clienteId);
      if (!next) return;
      setErpClient(next);
      if (phone) storeSelectedClienteId(phone, clienteId);
    },
    [erpClients, phone]
  );

  return {
    erpConfigured,
    erpClient,
    erpClients,
    selectErpClient,
    erpFrontendUrl,
    erpPendingCounts,
    loading
  };
};

export default useErpClient;
